import React, { useState } from "react";
import styled from "styled-components";
import useHomeworkReportView from "features/homework/hooks/useHomeworkReportView";
import PLAY_SVG from "assets/images/play.svg";
import { report, student } from "shared/types/Homework";
import useHomeworkStudentReportView from "features/homework/hooks/useHomeworkStudentReportview";
import HomeworkReportModal from "./HomeworkReportModal";
import Calendar from "./Calendar";
import { Button } from "shared/components/Button";

interface HomeworkLessonView{
    selectedLesson:string,
    onReportClickHandler:(report:report | undefined) => void,
    selectedStudent?:student,
}

interface FolderPathProps {
    inDrawer?: boolean;
}

const HomeworkStudentReportView = ({selectedLesson, selectedStudent, onReportClickHandler}:HomeworkLessonView) => {
    const [viewType, setViewType] = useState<"list" | "calendar">("calendar");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
    const [searchKeyword, setSearchKeyword] = useState("");
    const {reportList, isOpenLoadingModal, loadingProgress, isSupplementary, setIsSupplementary, loadError, retryLoad} = useHomeworkStudentReportView(selectedLesson, selectedStudent?.uid, selectedMonth);

    // 검색어로 필터링된 과제 목록
    const filteredBySearch = reportList.filter(report => 
        report.title.toLowerCase().includes(searchKeyword.toLowerCase())
    );

    // 선택된 달의 과제만 필터링
    const filteredReportList = filteredBySearch.filter(report => {
        const reportDate = report.date;
        return reportDate &&
               reportDate.getMonth() === selectedMonth.getMonth() && 
               reportDate.getFullYear() === selectedMonth.getFullYear();
    }).sort((a, b) => {
        // 날짜로 먼저 정렬
        const dateCompare = a.date.getTime() - b.date.getTime();
        if (dateCompare !== 0) return dateCompare;
        // 같은 날짜인 경우 이름순 정렬
        return a.title.localeCompare(b.title, 'ko');
    });

    // 날짜별로 과제 매핑
    const eventsByDate = filteredReportList.reduce((acc, report) => {
        const dateStr = report.date.toISOString().split('T')[0];
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(report);
        // 같은 날짜 내에서 이름순 정렬
        acc[dateStr].sort((a, b) => a.title.localeCompare(b.title, 'ko'));
        return acc;
    }, {} as Record<string, typeof filteredReportList>);

    const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const [year, month] = event.target.value.split('-').map(Number);
        setSelectedMonth(new Date(year, month - 1));
        setSelectedDate(null);
    };

    return(
        <Container>
            <HeaderContainer>
                <TabWrapper>
                    <TabButton active={viewType === "list"} onClick={() => setViewType("list")}>
                        리스트로 보기
                    </TabButton>
                    <TabButton active={viewType === "calendar"} onClick={() => setViewType("calendar")}>
                        캘린더로 보기
                    </TabButton>
                </TabWrapper>
                <ControlsContainer>
                    <AssignmentTypeToggle>
                        <ToggleButton 
                            active={!isSupplementary} 
                            onClick={() => setIsSupplementary(false)}
                        >
                            메인 과제
                        </ToggleButton>
                        <ToggleButton 
                            active={isSupplementary} 
                            onClick={() => setIsSupplementary(true)}
                        >
                            보충 과제
                        </ToggleButton>
                    </AssignmentTypeToggle>
                    {viewType === "list" && (
                        <SearchContainer>
                            <SearchInput
                                type="text"
                                placeholder="과제명으로 검색..."
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                            />
                        </SearchContainer>
                    )}
                    <MonthSelector value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`} onChange={handleMonthChange}>
                        {Array.from({length: 13}).map((_, i) => {
                            const currentDate = new Date();
                            const startYear = currentDate.getFullYear();
                            const startMonth = currentDate.getMonth(); // 0-based month
                            
                            // 이전 6개월부터 다음 6개월까지 (총 13개월)
                            const offset = i - 6; // -6부터 +6까지
                            const targetDate = new Date(startYear, startMonth + offset, 1);
                            const year = targetDate.getFullYear();
                            const month = targetDate.getMonth() + 1; // 1-based month
                            
                            return (
                                <option key={`${year}-${month}`} value={`${year}-${String(month).padStart(2, '0')}`}>
                                    {year}년 {month}월
                                </option>
                            );
                        })}
                    </MonthSelector>
                </ControlsContainer>
            </HeaderContainer>

            {isOpenLoadingModal ? (
                <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>과제 목록을 불러오는 중... {Math.round(loadingProgress)}%</LoadingText>
                </LoadingContainer>
            ) : loadError ? (
                <LoadingContainer role="alert">
                    <LoadingText>{loadError}</LoadingText>
                    <Button name="다시 시도" onButtonClick={retryLoad} />
                </LoadingContainer>
            ) : viewType === "list" ? (
                <Table>
                    <TableHeader>
                        <TableColumn style={{width:"25%"}}>과제명</TableColumn>
                        <TableColumn style={{width:"35%"}}>날짜</TableColumn>
                        <TableColumn style={{width:"20%"}}>진행률</TableColumn>
                        <TableColumn style={{width:"15%"}}>자세히 보기</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {filteredReportList.length === 0 ? (
                            <EmptyContainer>
                                <EmptyText>
                                    {searchKeyword 
                                        ? `"${searchKeyword}" 검색 결과가 없습니다` 
                                        : `${selectedMonth.getFullYear()}년 ${selectedMonth.getMonth() + 1}월 과제가 없습니다`
                                    }
                                </EmptyText>
                            </EmptyContainer>
                        ) : (
                            filteredReportList.map((item) => (
                                <TableRow key={item.id}>
                                    {item.folderPath && (
                                        <FolderPath inDrawer={false}>
                                            {item.folderPath.map((folder, index) => (
                                                <React.Fragment key={index}>
                                                    {index > 0 && <FolderSeparator>/</FolderSeparator>}
                                                    {folder}
                                                </React.Fragment>
                                            ))}
                                        </FolderPath>
                                    )}
                                    <RowContent>
                                        <TableColumn style={{width:"25%"}}>{item.title}</TableColumn>
                                        <TableColumn style={{width:"35%"}}>{new Date(item.date).toLocaleDateString()}</TableColumn>
                                        <TableColumn style={{width:"20%"}}>
                                            <ProgressContainer>
                                                <ProgressBar progress={item.progress || 0} />
                                                <ProgressText>{item.progress || 0}%</ProgressText>
                                            </ProgressContainer>
                                        </TableColumn>
                                        <TableColumn style={{width:"15%"}}>
                                            <DetailButton 
                                                src={PLAY_SVG} 
                                                alt="detail button" 
                                                onClick={() => onReportClickHandler(item)}
                                            />
                                        </TableColumn>
                                    </RowContent>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            ) : (
                <CalendarWrapper>
                    <Calendar
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                        events={eventsByDate}
                        activeStartDate={selectedMonth}
                        searchKeyword={searchKeyword}
                    />
                    {selectedDate && (
                        <DrawerContainer onClick={() => setSelectedDate(null)}>
                            <DrawerContent onClick={(e) => e.stopPropagation()}>
                                <DrawerHeader>
                                    <DrawerTitle>
                                        {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 과제
                                    </DrawerTitle>
                                    <CloseButton onClick={() => setSelectedDate(null)}>×</CloseButton>
                                </DrawerHeader>
                                <DrawerBody>
                                    {(eventsByDate[selectedDate.toISOString().split("T")[0]] || []).length === 0 ? (
                                        <EmptyDrawerText>해당 날짜에 과제가 없습니다</EmptyDrawerText>
                                    ) : (
                                        <DrawerList>
                                            {(eventsByDate[selectedDate.toISOString().split("T")[0]] || []).map((item) => (
                                                <DrawerItem key={item.id}>
                                                    <DrawerItemContent>
                                                        {item.folderPath && (
                                                            <FolderPath inDrawer={true}>
                                                                {item.folderPath.map((folder, index) => (
                                                                    <React.Fragment key={index}>
                                                                        {index > 0 && <FolderSeparator>/</FolderSeparator>}
                                                                        {folder}
                                                                    </React.Fragment>
                                                                ))}
                                                            </FolderPath>
                                                        )}
                                                        <DrawerItemTitle>{item.title}</DrawerItemTitle>
                                                        <DrawerItemProgress>
                                                            <ProgressBar progress={item.progress || 0} />
                                                            <ProgressText>{item.progress || 0}%</ProgressText>
                                                        </DrawerItemProgress>
                                                    </DrawerItemContent>
                                                    <DrawerItemActions>
                                                        <DetailButton
                                                            src={PLAY_SVG}
                                                            alt="detail button"
                                                            onClick={() => onReportClickHandler(item)}
                                                        />
                                                    </DrawerItemActions>
                                                </DrawerItem>
                                            ))}
                                        </DrawerList>
                                    )}
                                </DrawerBody>
                            </DrawerContent>
                        </DrawerContainer>
                    )}
                </CalendarWrapper>
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04);
    min-height: 0;
`

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
    animation: fadeInUp 0.5s ease-in-out;
    background: #FFFFFF;
    border-radius: 8px;
    overflow: hidden;
`

const TableHeader = styled.div`
    display: flex;
    background: #F8FAFC;
    border-bottom: 2px solid #E2E8F0;
    position: sticky;
    top: 0;
    z-index: 1;
`

const TableColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 8px 16px;
    font-size: 14px;
    color: #64748B;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TableBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #F1F5F9;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 3px;
    }
`

const TableRow = styled.div<{selected?:boolean}>`
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid #E2E8F0;
    background-color: ${(props) => props.selected ? "#FEF2F2" : "#FFFFFF"};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F8FAFC;
    }
`

const FolderPath = styled.div<FolderPathProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: ${props => props.inDrawer ? '4px 0px' : '4px 16px'};
    font-size: 12px;
    color: #64748B;
`

const FolderSeparator = styled.span`
    color: #94A3B8;
`

const RowContent = styled.div`
    display: flex;
    width: 100%;
`

const DetailButton = styled.img`
    width: 30px;
    height: 30px;
    padding: 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F1F5F9;
        transform: scale(1.1);
    }
    
    &:active {
        transform: scale(0.95);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
`

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid #E2E8F0;
    border-top: 3px solid #DF0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`

const LoadingText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

const EmptyContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
`

const EmptyText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

const ProgressContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 8px;
`

const ProgressBar = styled.div<{progress: number}>`
    flex-grow: 1;
    height: 8px;
    background: #E2E8F0;
    border-radius: 4px;
    overflow: hidden;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.progress}%;
        background: ${props => {
            if (props.progress >= 80) return '#DF0000';
            if (props.progress >= 40) return '#f97316';
            return '#f59e0b';
        }};
        border-radius: 4px;
        transition: width 0.3s ease, background-color 0.3s ease;
    }
`

const ProgressText = styled.span`
    font-size: 14px;
    color: #64748B;
    font-weight: 500;
    min-width: 48px;
    text-align: right;
`

const ContentProgressList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #E2E8F0;
`

const ContentProgressItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const ContentTypeLabel = styled.span`
    font-size: 12px;
    color: #64748B;
    min-width: 40px;
`

const ContentProgressBar = styled.div<{progress: number}>`
    flex-grow: 1;
    height: 4px;
    background: #E2E8F0;
    border-radius: 2px;
    overflow: hidden;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.progress}%;
        background: #DF0000;
        border-radius: 2px;
        transition: width 0.3s ease;
    }
`

const ContentProgressText = styled.span`
    font-size: 11px;
    color: #64748B;
    min-width: 30px;
    text-align: right;
`

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`

const TabWrapper = styled.div`
    display: flex;
    gap: 10px;
`

const TabButton = styled.button<{active: boolean}>`
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: ${props => props.active ? '#DF0000' : '#F8FAFC'};
    color: ${props => props.active ? '#FFFFFF' : '#64748B'};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.active ? '#DF0000' : '#F1F5F9'};
    }
`

const ControlsContainer = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`

const SearchContainer = styled.div`
    position: relative;
`

const SearchInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    color: #1E293B;
    font-size: 14px;
    width: 200px;
    outline: none;

    &:focus {
        border-color: #DF0000;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    &::placeholder {
        color: #94A3B8;
    }
`

const MonthSelector = styled.select`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    color: #1E293B;
    font-size: 14px;
    cursor: pointer;
    outline: none;

    &:focus {
        border-color: #DF0000;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }
`

const CalendarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;
    width: 100%;
`

const DrawerContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`

const DrawerContent = styled.div`
    background: #FFFFFF;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const DrawerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
`

const DrawerTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #1E293B;
    margin: 0;
`

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    font-weight: 400;
    cursor: pointer;
    color: #64748B;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
        background: #E2E8F0;
        color: #1E293B;
    }
`

const DrawerBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex-grow: 1;
`

const EmptyDrawerText = styled.div`
    color: #64748B;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    padding: 40px 20px;
`

const DrawerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const DrawerItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #F8FAFC;
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    transition: all 0.2s ease;

    &:hover {
        background: #F1F5F9;
        border-color: #CBD5E1;
    }
`

const DrawerItemContent = styled.div`
    flex: 1;
    margin-right: 16px;
`

const DrawerItemTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    color: #1E293B;
    margin: 0 0 8px 0;
`

const DrawerItemProgress = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const DrawerItemActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`

const AssignmentTypeToggle = styled.div`
    display: flex;
    gap: 2px;
    background: #F1F5F9;
    border-radius: 8px;
    padding: 2px;
`;

const ToggleButton = styled.button<{active: boolean}>`
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    background: ${props => props.active ? '#DF0000' : 'transparent'};
    color: ${props => props.active ? '#FFFFFF' : '#64748B'};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: ${props => props.active ? '0 1px 3px rgba(220, 38, 38, 0.2)' : 'none'};

    &:hover {
        background: ${props => props.active ? '#DF0000' : '#E2E8F0'};
    }
`;

export default HomeworkStudentReportView;
